import { useMemo, useState } from 'react'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import Modal from 'react-modal'
import moment from 'moment'
import { useTranslation } from 'react-i18next'

import SubmitButton from '../../../../common/buttons/SubmitButton'
import { displayWithCurrency } from '../../../../../utility/currency'
import { ReactComponent as WarningIcon } from '../../../../../assets/images/common/icons/warning-icon.svg'
import { ReactComponent as ModalXIcon } from '../../../../../assets/images/common/icons/modal-X-icon.svg'
import './RefundEnrollmentModal.scss'
import Input from '../../../../common/form/Input'
import MCElement from '../../../../common/MCElement'


const RefundEnrollmentModal = ({
  showModal,
  closeModal,
  coachMessage,
  handleCoachMessageChange,
  handleRemoveEnrollment,
  enrollment,
  notifyViaEmail,
  handleCheckboxChange,
  isLoading,
}) => {
  const { t } = useTranslation()
  const [refundEnrollment, setRefundEnrollment] = useState(false)
  const transactionDetails = useMemo(() => ([
    { label: t('programs.common.fields.golfer'), value: `${enrollment.user.firstName} ${enrollment.user.lastName}` },
    { label: t('common.field.date'), value: moment(enrollment.paid_at).format('MM/DD/YYYY') },
    { label: t('programs.common.fields.amount'), value: displayWithCurrency(enrollment.transactionAmount, enrollment.currency) },
    { label: t('programs.manage.refundEnrollment.stripeFee'), value: displayWithCurrency(enrollment.fees.stripeFee, enrollment.convertedCurrency) },
    { label: t('programs.manage.refundEnrollment.applicationFee'), value: displayWithCurrency(enrollment.fees.applicationFee, enrollment.currency) },
  ]), [enrollment, t])

  const handleDropEnrollmentClick = () => {
    handleRemoveEnrollment(refundEnrollment)
  }

  return (
    <Modal
      isOpen={showModal}
      onRequestClose={closeModal}
      contentLabel={t('programs.manage.refundEnrollment.modalLabel')}
      className={classNames('refund-enrollment-modal-content', { 'second-modal-screen-content': refundEnrollment })}
      autoFocus
    >
      <div id='RefundEnrollmentModal'>
        <div className='animated fadeIn'>
          <div className='d-flex justify-content-end text-align-right'>
            <ModalXIcon id='modalXIcon' onClick={closeModal} />
          </div>
          <div className='d-flex flex-column justify-content-center align-items-center'>
            <WarningIcon id='warningIcon' />
            <p className='title'>
              {refundEnrollment
                ? t('programs.manage.refundEnrollment.dropRefundTitle')
                : t('programs.manage.refundEnrollment.dropTitle')
              }
            </p>
            <p className='sub-title'>
              {refundEnrollment
                ? t('programs.manage.refundEnrollment.dropRefundSubtitle')
                : t('programs.manage.refundEnrollment.dropSubtitle')
              }
            </p>
          </div>
          {refundEnrollment &&
            <>
              <p className='sub-title mt-2'>
                {t('programs.manage.refundEnrollment.refundDisclaimer')}
              </p>
              <p className='transaction-details'>{t('programs.manage.refundEnrollment.transactionDetails')}</p>
              <div className='divider' />
              {transactionDetails.map(({ label, value }) =>
                <div className='d-flex justify-content-between align-items-center' key={label}>
                  <p className='key'>{label}</p>
                  <p className='value'>{value}</p>
                </div>
              )}
            </>
          }
          <div className='d-flex flex-column justify-content-center align-items-center'>
            <p className='sub-title mt-4'>{t('programs.manage.refundEnrollment.additionalMessagePrompt')}</p>
            <Input
              name='description'
              type='textarea'
              onChange={handleCoachMessageChange}
              placeholder={t('programs.common.customMessageFamily')}
              value={coachMessage}
              classNames={{ inputContainer: 'coachMessageInputContainer', inputContainerWrapper: 'mt-3 coachMessageWrapper' }}
            />
          </div>
          <div>
            <div className='row py-3 justify-content-center align-items-center'>
              <MCElement
                type='checkbox'
                label={t('programs.manage.refundEnrollment.doNotNotifyFamily')}
                isSelected={!notifyViaEmail}
                clicked={handleCheckboxChange}
              />
              <div className='col-12'>
                <SubmitButton
                  onClick={refundEnrollment ? handleDropEnrollmentClick : () => setRefundEnrollment(true)}
                  buttonMessage={t('programs.manage.refundEnrollment.dropAndRefund')}
                  buttonColor='orange-button'
                  loading={refundEnrollment && isLoading}
                  disabled={isLoading}
                />
              </div>
              <div className='col-12'>
                <SubmitButton
                  onClick={refundEnrollment ? closeModal : handleDropEnrollmentClick}
                  buttonMessage={refundEnrollment ? t('common.close') : t('programs.manage.refundEnrollment.dropNoRefund')}
                  buttonColor='navy-button'
                  loading={!refundEnrollment && isLoading}
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}

RefundEnrollmentModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  coachMessage: PropTypes.string,
  handleCoachMessageChange: PropTypes.func.isRequired,
  handleRemoveEnrollment: PropTypes.func.isRequired,
  enrollment: PropTypes.object.isRequired,
  notifyViaEmail: PropTypes.bool.isRequired,
  handleCheckboxChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
}

export default RefundEnrollmentModal
